import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { contactsApi } from 'src/Services/API';

const CONTACTS_PER_PAGE = 10;

/**
 * All contacts of the current asset; `search` filters server-side (names, company,
 * interlocutors, SIRET, customer reference, phone), accents and case ignored.
 */
const useFetchContacts = (search?: string) => {
  const assetId = useCurrentAssetId();

  const query = useInfiniteQuery({
    queryKey: ['fetch-contacts', assetId, search ?? ''],
    queryFn: async ({ pageParam = 1 }) => {
      if (!assetId) throw new Error('No assetId provided');

      return (
        await contactsApi.contactsControllerFindAll(
          assetId,
          pageParam,
          CONTACTS_PER_PAGE,
          undefined,
          undefined,
          search || undefined
        )
      ).data;
    },
    getNextPageParam: lastPage => {
      if (!lastPage) return undefined;
      return lastPage.currentPage < lastPage.pageCount ? lastPage.currentPage + 1 : undefined;
    },
    enabled: !!assetId,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    const loadAllPages = async () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        await query.fetchNextPage();
      }
    };

    loadAllPages();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const allContacts = useMemo(() => {
    const items = query.data?.pages.flatMap(page => page?.items ?? []) ?? [];
    const seen = new Set<string>();
    return items.filter(contact => {
      if (seen.has(contact.id)) return false;
      seen.add(contact.id);
      return true;
    });
  }, [query.data]);

  return {
    data: allContacts,
    isLoading: query.isLoading || query.isFetchingNextPage,
    error: query.error,
  };
};
export default useFetchContacts;
